import React, { useRef } from "react";
import { SectionShell } from "./PartnerFormPrimitives";
import { Upload, FileText, CheckCircle2, X } from "lucide-react";
import { fileToBase64, formatBytes } from "./partnerHelpers";

function FileUploadField({ label, required, hint, fileKey, file, onChange, onError, testId }) {
  const inputRef = useRef(null);

  const handlePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const payload = await fileToBase64(f);
      onChange(fileKey, payload);
    } catch (err) {
      onError(err?.message || "Could not read the selected file.");
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const handleClear = () => {
    onChange(fileKey, null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="border border-dashed border-gray-300 rounded p-4 bg-white" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="label-br mb-0">{label}{required && <span className="text-br-bronze ml-0.5">*</span>}</div>
          {hint && <div className="text-[11px] text-gray-500">{hint}</div>}
        </div>
        {file && <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />}
      </div>
      {file ? (
        <div className="flex items-center justify-between bg-br-paper border border-gray-200 rounded p-2 text-xs">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={14} className="text-br-bronze shrink-0" />
            <span className="truncate" title={file.filename}>{file.filename}</span>
            <span className="text-gray-500 shrink-0">· {formatBytes(file.size)}</span>
          </div>
          <button type="button" onClick={handleClear} className="text-gray-400 hover:text-red-600 shrink-0" aria-label="Remove file">
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 border border-gray-300 hover:border-br-bronze rounded p-3 text-sm text-gray-700 transition"
        >
          <Upload size={14} /> Choose file
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
        onChange={handlePick}
      />
    </div>
  );
}

export default function PartnerUploads({ data, setFile, onError }) {
  return (
    <SectionShell
      index={7}
      title="Insurance & Compliance Uploads"
      subtitle="PDF or image. 3MB max per file. We'll request originals before your first project."
      testId="partner-section-uploads"
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <FileUploadField label="W-9 Form" required fileKey="w9_file" file={data.w9_file} onChange={setFile} onError={onError} testId="partner-upload-w9" />
        <FileUploadField label="Certificate of Insurance (COI)" required fileKey="coi_file" file={data.coi_file} onChange={setFile} onError={onError} testId="partner-upload-coi" />
        <FileUploadField label="Workers Compensation" hint="Required if you have employees" fileKey="wc_file" file={data.wc_file} onChange={setFile} onError={onError} testId="partner-upload-wc" />
        <FileUploadField label="Government ID or Driver's License" required fileKey="id_file" file={data.id_file} onChange={setFile} onError={onError} testId="partner-upload-id" />
      </div>
    </SectionShell>
  );
}
